import React, { useCallback, useRef } from 'react';
import { Modal, Form, DatePicker, TimePicker, Input, Button, Typography, Tag } from 'antd';
import { CalendarOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

const { Text } = Typography;

const BookingModal = ({ open, product, onCancel, onConfirm, loading }) => {
  const [form] = Form.useForm();
  const confirming = useRef(false);

  const handleOk = useCallback(async () => {
    if (confirming.current) return;
    try {
      const values = await form.validateFields();
      confirming.current = true;
      await onConfirm({
        booking_date: values.booking_date.format('YYYY-MM-DD'),
        booking_time: values.booking_time.format('HH:mm'),
        notes: values.notes || null,
        address: values.address || null,
        contact_phone: values.contact_phone || null,
      });
      form.resetFields();
    } catch (_) {
    } finally {
      confirming.current = false;
    }
  }, [form, onConfirm]);

  const handleCancel = useCallback(() => {
    form.resetFields();
    onCancel();
  }, [form, onCancel]);

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <CalendarOutlined style={{ color: '#52b788' }} />
          <span>Book Service</span>
          {product && (
            <Tag color="blue" style={{ marginLeft: 8 }}>
              {product.name || product.product_service?.name}
            </Tag>
          )}
        </div>
      }
      open={open}
      onCancel={handleCancel}
      footer={null}
      width={480}
      destroyOnClose
      maskClosable={false}
      closable={!loading}
    >
      {product && (
        <div
          style={{
            marginBottom: 20,
            padding: 16,
            background: '#fafff5',
            borderRadius: 12,
            border: '1px solid #e8f0e8',
          }}
        >
          <Text style={{ color: '#6b7280', fontSize: 13 }}>
            This is a service, please select your preferred date and time for the appointment.
          </Text>
        </div>
      )}
      <Form form={form} layout="vertical">
        <Form.Item
          name="booking_date"
          label="Preferred Date"
          rules={[{ required: true, message: 'Please select a date' }]}
        >
          <DatePicker
            style={{ width: '100%', borderRadius: 10 }}
            minDate={dayjs()}
            placeholder="Select date"
          />
        </Form.Item>
        <Form.Item
          name="booking_time"
          label="Preferred Time"
          rules={[{ required: true, message: 'Please select a time' }]}
        >
          <TimePicker
            style={{ width: '100%', borderRadius: 10 }}
            format="HH:mm"
            minuteStep={15}
            placeholder="Select time"
          />
        </Form.Item>
        <Form.Item
          name="contact_phone"
          label="Contact Phone"
          rules={[{ pattern: /^\+?\d+$/, message: 'Please enter numbers and + only' }]}
        >
          <Input
            type="tel"
            onKeyPress={(e) => e.key !== '+' && !/[0-9]/.test(e.key) && e.preventDefault()}
            placeholder="Phone number for service appointment"
            style={{ borderRadius: 10 }}
          />
        </Form.Item>
        <Form.Item name="address" label="Service Address">
          <Input placeholder="Address where service is needed" style={{ borderRadius: 10 }} />
        </Form.Item>
        <Form.Item name="notes" label="Notes / Special Requirements">
          <Input.TextArea
            rows={3}
            placeholder="Any special requirements or notes"
            style={{ borderRadius: 10 }}
          />
        </Form.Item>
        <Button
          type="primary"
          block
          size="large"
          loading={loading}
          onClick={handleOk}
          icon={<CalendarOutlined />}
          style={{
            height: 46,
            borderRadius: 10,
            background: 'linear-gradient(135deg, #52b788, #2d5a3d)',
            border: 'none',
            fontWeight: 600,
          }}
        >
          Add to Cart with Booking
        </Button>
      </Form>
    </Modal>
  );
};

export default BookingModal;
